import bufferToBlob from 'audiobuffer-to-blob';

/**
 * Downloads a given file as an MP3 with the specified name.
 *
 * @param {File} file - The file to be downloaded.
 * @param {string} name - The name to be given to the downloaded file (without extension).
 */
export const mp3Download = (file: File, name: string) => {
  const url = window.URL.createObjectURL(file)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = name + '.mp3'
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }, 100)
}

/**
 * Converts an audio buffer to an MP3 file.
 *
 * @param {ArrayBuffer} buffer - The audio buffer to convert.
 * @param {string} name - The name of the resulting MP3 file.
 * @returns {Promise<File>} A promise that resolves to the converted MP3 file.
 *
 */
const mp3Converter = async (buffer: ArrayBuffer, name: string) => {
  const audioContext = new window.AudioContext()
  const sampleRate = 8000
  const numberOfChannels = 1
  const decodedAudioData = await audioContext.decodeAudioData(
    buffer as ArrayBuffer,
  )
  const duration = decodedAudioData.duration
  const offlineAudioContext = new OfflineAudioContext(
    numberOfChannels,
    sampleRate * duration,
    sampleRate,
  )
  const soundSource = offlineAudioContext.createBufferSource()
  const decodedBuffer = decodedAudioData
  soundSource.buffer = decodedBuffer
  soundSource.connect(offlineAudioContext.destination)
  soundSource.start()
  const renderedBuffer = await offlineAudioContext.startRendering()
  const blob = bufferToBlob(renderedBuffer)
  return new File([blob as Blob], name + '.mp3', {
    type: 'audio/mp3',
  })
}

/**
 * Extracts audio from a given video file and converts it to MP3 format.
 *
 * @param {File} file - The video file from which to extract audio.
 * @returns {Promise<any>} A promise that resolves with the converted MP3 audio data.
 */
export const audioExtract = async (file: File) => {
  const reader = new FileReader()
  return new Promise((resolve) => {
    reader.onload = () => {
      resolve(mp3Converter(reader.result as ArrayBuffer, file.name))
    }
    reader.readAsArrayBuffer(file)
  })
}